import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import useSWR from "swr";
import Layout from "./Layout";
import FriendRequests from "./components/FriendRequests";
import useInitGoogleAuth from "./lib/hooks/useInitGoogleAuth";
import useRefreshLocation from "./lib/hooks/useRefreshLocation";
import useRefreshPermille from "./lib/hooks/useRefreshPermille";
import useRegisterServiceWorker from "./lib/hooks/useRegisterServiceWorker";
import { User, patchUser } from "./lib/slices/userSlice";
import { useSyncReduxAll } from "./lib/syncAll";
import Friends from "./pages/Friends";
import Home from "./pages/Home";
import Nearby from "./pages/Nearby";
import OwnDrinks from "./pages/OwnDrinks";
import PartyPage from "./pages/Party";
import Session from "./pages/Session";
import Sessions from "./pages/Sessions";
import UserPage from "./pages/User";

const registerFetcher = (token?: string) => (url) =>
  fetch(url, {
    method: "POST",
    body: JSON.stringify({ token }),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  }).then((res) => res.json());

export default function App() {
  const postHog = usePostHog();

  const dispatch = useDispatch();

  const [updateAvailable, setUpdateAvailable] = useState(false);

  const user: User = useSelector((state: any) => state.user);

  const shouldFetch = !user.token;
  const { data } = useSWR(
    shouldFetch
      ? `${process.env.REACT_APP_API_URL}/users/register`
      : `${process.env.REACT_APP_API_URL}/users/get-user`,
    registerFetcher(user?.token),
    {
      refreshInterval: shouldFetch ? 10000 : 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  useEffect(() => {
    if (!user.id) return;

    if (process.env.NODE_ENV === "development") {
      postHog.opt_out_capturing();
      return;
    }

    postHog.identify(user.id, {
      tag: user.tag,
      name: user.name,
    });
  }, [postHog, user.id, user.tag, user.name]);

  useEffect(() => {
    if (!data) return;

    if (
      data.id !== user.id ||
      data.token !== user.token ||
      (data.tag || undefined) !== (user.tag || undefined)
    )
      dispatch(
        patchUser({
          id: data.id,
          token: data.token,
          tag: data.tag || null,
        })
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useRefreshPermille();

  useSyncReduxAll();

  useRefreshLocation();

  useInitGoogleAuth();

  useRegisterServiceWorker({
    setUpdateAvailable,
  });

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Layout updateAvailable={updateAvailable} />}>
          <Route index element={<Home />} />
          <Route path="/nearby" element={<Nearby />} />
          <Route path="/own-drinks" element={<OwnDrinks />} />
          <Route path="/friend-requests" element={<FriendRequests />} />
          <Route path="/friends" element={<Friends />} />
          <Route path="/sessions" element={<Sessions />} />
          <Route path="/session/:sessionId" element={<Session />} />
          <Route path="/user/:userId" element={<UserPage />} />
          {/* <Route path="/parties" element={<Parties />} /> */}
          <Route path="/party/:partyId" element={<PartyPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
